import React, { useState } from "react";
import { motion } from "framer-motion";
import { Calculator, Info, MapPin } from "lucide-react";
import {
  SERVICE_TOWNS,
  MILEAGE_RATE,
  MINIMUM_FEE,
  computeQuote,
} from "../lib/towns";

function NumberField({ label, sub, value, onChange, testid }) {
  const dec = () => onChange(Math.max(0, Number(value) - 1));
  const inc = () => onChange(Number(value) + 1);
  return (
    <div className="flex items-center justify-between gap-4 py-4 border-b border-black/5 last:border-b-0">
      <div>
        <div className="text-[#111827] font-medium">{label}</div>
        <div className="text-xs text-[#4B5563] mt-0.5">{sub}</div>
      </div>
      <div className="flex items-center gap-2">
        <button
          onClick={dec}
          className="h-9 w-9 rounded-full border border-black/10 text-[#1A365D] hover:bg-[#E8F1F2] transition-colors"
          data-testid={`${testid}-dec`}
          aria-label={`decrease ${label}`}
        >
          −
        </button>
        <input
          type="number"
          min={0}
          value={value}
          onChange={(e) => onChange(Math.max(0, Number(e.target.value) || 0))}
          className="w-16 text-center rounded-xl border border-black/10 bg-white py-2 font-serif text-xl text-[#111827] focus:outline-none focus:ring-2 focus:ring-[#1A365D]/30"
          data-testid={`${testid}-input`}
        />
        <button
          onClick={inc}
          className="h-9 w-9 rounded-full border border-[#1A365D] bg-[#1A365D] text-white hover:bg-[#2B4C7E] transition-colors"
          data-testid={`${testid}-inc`}
          aria-label={`increase ${label}`}
        >
          +
        </button>
      </div>
    </div>
  );
}

export default function QuoteCalculator({ value, onChange }) {
  const [localTownName, setLocalTownName] = useState(value.town.name);
  const [manualMiles, setManualMiles] = useState(value.manualMiles || "");

  const q = computeQuote(value);

  const update = (patch) => onChange({ ...value, ...patch });

  const handleTownChange = (name) => {
    setLocalTownName(name);
    const town = SERVICE_TOWNS.find((t) => t.name === name) || SERVICE_TOWNS[0];
    update({ town, manualMiles: town.oneWay === -1 ? manualMiles : "" });
  };

  const isManual = value.town?.oneWay === -1;

  return (
    <section
      id="quote"
      className="bg-[#F9F9F6] py-24 md:py-32 border-t border-black/5"
      data-testid="quote-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-12 mb-4">
            <div className="text-xs uppercase tracking-[0.25em] font-semibold text-[#1A365D] mb-4">
              Interactive Quote
            </div>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight text-[#111827] leading-tight max-w-3xl">
              Build your estimate in under a minute.
            </h2>
          </div>

          {/* Calculator card */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-7 rounded-3xl bg-white/90 backdrop-blur-xl border border-white shadow-[0_20px_60px_rgba(0,0,0,0.06)] p-8 md:p-10"
            data-testid="quote-calculator-widget"
          >
            <div className="flex items-center gap-3 mb-6">
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-[#E8F1F2] text-[#1A365D]">
                <Calculator className="h-5 w-5" />
              </span>
              <div>
                <div className="font-serif text-2xl text-[#111827]">Window counts</div>
                <div className="text-sm text-[#4B5563]">
                  Count each pane — we&apos;ll confirm on-site. Commercial jobs
                  are quoted separately — just send your details.
                </div>
              </div>
            </div>

            <NumberField
              label="1st-story outside"
              sub="$10 per window"
              value={value.singleOut}
              onChange={(v) => update({ singleOut: v })}
              testid="calc-single-out"
            />
            <NumberField
              label="Inside windows"
              sub="$7 per window"
              value={value.inside}
              onChange={(v) => update({ inside: v })}
              testid="calc-inside"
            />
            <NumberField
              label="2nd-story outside"
              sub="$20 per window"
              value={value.secondOut}
              onChange={(v) => update({ secondOut: v })}
              testid="calc-second-out"
            />

            <div className="mt-8 border-t border-black/5 pt-6">
              <div className="flex items-center gap-3 mb-4">
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-[#E8F1F2] text-[#1A365D]">
                  <MapPin className="h-5 w-5" />
                </span>
                <div>
                  <div className="font-serif text-2xl text-[#111827]">Service area</div>
                  <div className="text-sm text-[#4B5563]">
                    Free inside San Angelo. Outside — $3/mile round trip.
                  </div>
                </div>
              </div>

              <label className="block text-xs uppercase tracking-[0.15em] text-[#4B5563] mb-2">
                Your town
              </label>
              <select
                value={localTownName}
                onChange={(e) => handleTownChange(e.target.value)}
                className="w-full rounded-2xl border border-black/10 bg-white px-4 py-3 text-[#111827] focus:outline-none focus:ring-2 focus:ring-[#1A365D]/30"
                data-testid="calc-town-select"
              >
                {SERVICE_TOWNS.map((t) => (
                  <option key={t.name} value={t.name}>
                    {t.name}
                    {t.oneWay > 0 ? `  —  ~${t.oneWay} mi one-way` : ""}
                  </option>
                ))}
              </select>

              {isManual && (
                <div className="mt-4">
                  <label className="block text-xs uppercase tracking-[0.15em] text-[#4B5563] mb-2">
                    One-way miles from San Angelo
                  </label>
                  <input
                    type="number"
                    min={1}
                    value={manualMiles}
                    onChange={(e) => {
                      setManualMiles(e.target.value);
                      update({ manualMiles: e.target.value });
                    }}
                    placeholder="e.g. 40"
                    className="w-full rounded-2xl border border-black/10 bg-white px-4 py-3 text-[#111827] focus:outline-none focus:ring-2 focus:ring-[#1A365D]/30"
                    data-testid="calc-manual-miles"
                  />
                </div>
              )}
            </div>

            <div className="mt-8 rounded-2xl bg-[#E8F1F2] border border-[#1A365D]/10 p-5 flex gap-3">
              <Info className="h-5 w-5 text-[#1A365D] shrink-0 mt-0.5" />
              <p className="text-sm text-[#1A365D]/90 leading-relaxed">
                This is a <strong>rough estimate</strong> for a residential
                visit. One of our insured technicians will confirm the final
                price on-site. <strong>${MINIMUM_FEE} minimum</strong> visit
                fee applies.
              </p>
            </div>
          </motion.div>

          {/* Summary card */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="lg:col-span-5"
          >
            <div
              className="sticky top-28 rounded-3xl bg-[#1A365D] text-white p-8 md:p-10 shadow-[0_30px_60px_rgba(26,54,93,0.25)]"
              data-testid="quote-summary"
            >
              <div className="text-xs uppercase tracking-[0.25em] text-white/70 mb-3">
                Your estimate
              </div>
              <div
                className="font-serif text-6xl md:text-7xl leading-none"
                data-testid="quote-total"
              >
                ${q.total.toFixed(0)}
              </div>
              <div className="mt-6 h-px bg-white/15" />

              <div className="mt-6 space-y-3 text-white/90">
                {value.singleOut > 0 && (
                  <Row
                    label={`1st-story outside × ${value.singleOut}`}
                    v={`$${q.singleOutTotal.toFixed(0)}`}
                  />
                )}
                {value.inside > 0 && (
                  <Row
                    label={`Inside × ${value.inside}`}
                    v={`$${q.insideTotal.toFixed(0)}`}
                  />
                )}
                {value.secondOut > 0 && (
                  <Row
                    label={`2nd-story outside × ${value.secondOut}`}
                    v={`$${q.secondOutTotal.toFixed(0)}`}
                  />
                )}
                {q.windowSubtotal > 0 && (
                  <Row
                    label="Windows subtotal"
                    v={`$${q.windowSubtotal.toFixed(0)}`}
                  />
                )}
                {q.minimumApplied && (
                  <Row
                    label={`Minimum visit fee`}
                    v={`$${MINIMUM_FEE}`}
                  />
                )}
                {q.mileageFee > 0 && (
                  <Row
                    label={`Mileage (${q.roundTrip} mi RT × $${MILEAGE_RATE})`}
                    v={`$${q.mileageFee.toFixed(0)}`}
                  />
                )}
              </div>

              {q.minimumApplied && (
                <p className="mt-6 text-xs text-white/60 leading-relaxed">
                  Window charges under ${MINIMUM_FEE} are covered by the
                  minimum visit fee.
                </p>
              )}

              <a
                href="#request"
                onClick={(e) => {
                  e.preventDefault();
                  const el = document.getElementById("request");
                  if (el) el.scrollIntoView({ behavior: "smooth" });
                }}
                className="mt-8 inline-flex items-center justify-center w-full rounded-full bg-white text-[#1A365D] uppercase tracking-[0.18em] text-sm py-4 hover:bg-[#E8F1F2] transition-colors"
                data-testid="quote-book-cta"
              >
                Request a visit →
              </a>
              <p className="mt-4 text-xs text-white/60 leading-relaxed">
                Submitting texts our team at 608-577-2761. We&apos;ll call back
                to confirm your Mon–Sat time.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function Row({ label, v }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-white/75">{label}</span>
      <span className="font-medium">{v}</span>
    </div>
  );
}
